import React, { useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  SelectChangeEvent,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { CRMAttributeConfig, CRMSettings } from '../../types';
import { FieldType } from '../../../../hooks/useSchema';
import { MoreActionsMenuButton } from '../../../primitives';
import { useEditItem2 } from '../../../../api/useNewItem';
import { SelectOptionsConfigurationDialog } from './SelectOptionsConfigurationDialog';

interface ContactAttributesEditorProps {
  item?: CRMSettings | null;
  update: (changes: Partial<CRMSettings>) => void;
}

export const ContactAttributesConfigurationEditor: React.FC<ContactAttributesEditorProps> = ({ item, update }) => {
  const attributes = item?.contact_attributes || [];
  const systemCategories = item?.contact_categories || [];

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleAdd = () => {
    update({
      contact_attributes: [
        ...attributes,
        { code: '', label: '', type: FieldType.text },
      ],
    });
  };

  const handleRemove = (indexToRemove: number) => {
    update({
      contact_attributes: attributes.filter((_, index) => index !== indexToRemove),
    });
  };

  const handleChange = (
    index: number,
    field: keyof CRMAttributeConfig,
    value: any
  ) => {
    const updatedAttributes = [...attributes];
    updatedAttributes[index] = {
      ...updatedAttributes[index],
      [field]: value,
    };
    update({ contact_attributes: updatedAttributes });
  };

  // Native Drag and Drop Handlers
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault(); // Necessary to allow dropping
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      const updatedAttributes = [...attributes];
      // Remove the dragged item and insert it at the drop location
      const [draggedItem] = updatedAttributes.splice(draggedIndex, 1);
      updatedAttributes.splice(dropIndex, 0, draggedItem);
      
      update({ contact_attributes: updatedAttributes });
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const editOptions = useEditItem2<CRMAttributeConfig & { idx: number }>({
    save: async (item, c) => {
      const cleanOptions = ((item.select_options || []) as any[]).map(({ _id, _isOriginal, ...rest }) => rest);
      handleChange(item.idx, "select_options", cleanOptions);
      return item;
    }
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      <Typography variant="caption" color="textSecondary">Атрибуты</Typography>
      {attributes.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          Нет добавленных атрибутов.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {attributes.map((attr, index) => {
            const isDragging = draggedIndex === index;
            const isDragOver = dragOverIndex === index && draggedIndex !== index;

            return (
              <Paper
                key={index}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                variant="outlined"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  p: 2,
                  backgroundColor: '#fafafa',
                  opacity: isDragging ? 0.4 : 1,
                  boxShadow: isDragOver 
                    ? (draggedIndex! > index ? '0 -2px 0 0 #1976d2' : '0 2px 0 0 #1976d2')
                    : 'none',
                  transition: 'box-shadow 0.2s, opacity 0.2s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'grab',
                      color: 'action.active',
                      '&:active': { cursor: 'grabbing' }
                    }}
                  >
                    <DragIndicatorIcon />
                  </Box>

                  <TextField
                    size="small"
                    label="Код (Code)"
                    value={attr.code}
                    onChange={(e) => handleChange(index, 'code', e.target.value)}
                    placeholder="e.g. internal_id"
                    fullWidth
                    sx={{ alignSelf: "flex-end" }}
                  />

                  <TextField
                    size="small"
                    label="Название (Label)"
                    value={attr.label}
                    onChange={(e) => handleChange(index, 'label', e.target.value)}
                    placeholder="e.g. Внутренний ID"
                    fullWidth
                    sx={{ alignSelf: "flex-end" }}
                  />

                  <Select
                    size="small"
                    value={attr.type}
                    onChange={(e: SelectChangeEvent) => handleChange(index, 'type', e.target.value)}
                    sx={{ minWidth: 150, alignSelf: "flex-end" }}
                    label="Тип"
                  >
                    <MenuItem value="text">Текст</MenuItem>
                    <MenuItem value="text-long">Длинный текст</MenuItem>
                    <MenuItem value="bool">Чекбокс</MenuItem>
                    <MenuItem value="select">Выбор</MenuItem>
                    <MenuItem value="suggest">Саджест</MenuItem>
                    <MenuItem value="suggest_multi">Саджест множественный</MenuItem>
                  </Select>

                  {!!systemCategories?.length &&
                    <Select
                      labelId={`categories-select-label-${index}`}
                      multiple
                      placeholder="Доступно для категорий"
                      sx={{ minWidth: 210, alignSelf: "flex-end" }}
                      value={attr.available_for_categories || []}
                      onChange={(e: SelectChangeEvent<string[]>) => {
                        const value = e.target.value;
                        handleChange(index, 'available_for_categories', typeof value === 'string' ? value.split(',') : value);
                      }}
                      renderValue={(selected: string[]) => {
                        if (selected.length === 0) return <em>Все категории</em>;
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((code) => {
                              const cat = systemCategories.find(c => c.code === code);
                              return <Chip key={code} label={cat?.label || code} size="small" />;
                            })}
                          </Box>
                        );
                      }}
                    >
                      {systemCategories.map((category) => (
                        <MenuItem key={category.code} value={category.code}>
                          {category.label || category.code}
                        </MenuItem>
                      ))}
                    </Select>}

                  <MoreActionsMenuButton
                    strippedButton
                    actions={[
                      ["remove", "удалить", () => handleRemove(index)],
                      attr.type === "select"
                        ? ["configure_options", "настроить значения", () => editOptions.startEditing({
                          idx: index,
                          ...attr,
                          select_options: (attr.select_options || []).map(opt => ({
                            ...opt,
                            _id: Math.random().toString(36).substring(2, 9),
                            _isOriginal: true,
                          })),
                        })]
                        : null,
                    ]}
                  />
                </Box>

                {/* Additional Row for Category Binding */}
                <Box sx={{ display: 'flex', alignItems: 'center', pl: 5.5, width: '100%' }}>
                  
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      <Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          size="small"
        >
          Добавить атрибут
        </Button>
      </Box>

      <SelectOptionsConfigurationDialog
        data={editOptions}
        />
    </Box>
  );
};